import { useState, useEffect, useMemo, useRef } from 'react';
import DashboardLayout from '../../components/feature/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../lib/api';
import { getStaticUrl } from '../../lib/api/client';
import { useFamilyData } from '../../lib/context/FamilyDataContext';
import { getPointsIcon, getPointsName } from '../../lib/utils/pointsIcon';
import type { DashboardResponse } from '../../lib/api';

type ModalType = 'reward' | 'punishment' | null;

interface RecordForm {
  childId: string;
  ruleId: string;
  note: string;
}

const channelConfig: Record<string, { bg: string; text: string; icon: string }> = {
  'AI记录': { bg: 'bg-violet-100', text: 'text-violet-700', icon: 'ri-robot-2-fill' },
  'AI': { bg: 'bg-violet-100', text: 'text-violet-700', icon: 'ri-robot-2-fill' },
  'PC': { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'ri-computer-line' },
  'pc': { bg: 'bg-slate-100', text: 'text-slate-600', icon: 'ri-computer-line' },
  '小程序': { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'ri-wechat-fill' },
  '系统': { bg: 'bg-gray-100', text: 'text-gray-600', icon: 'ri-settings-3-line' }
};

const typeConfig: Record<string, { bg: string; text: string; label: string }> = {
  reward: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '奖励' },
  punishment: { bg: 'bg-red-100', text: 'text-red-600', label: '惩罚' },
  redeem: { bg: 'bg-amber-100', text: 'text-amber-700', label: '兑换' }
};

function getScoreLabel(score: number) {
  if (score >= 100) return { label: '完美', color: 'text-yellow-200', bg: 'bg-yellow-400/20' };
  if (score >= 90) return { label: '优秀', color: 'text-emerald-300', bg: 'bg-emerald-400/20' };
  if (score >= 80) return { label: '良好', color: 'text-yellow-200', bg: 'bg-yellow-400/20' };
  if (score >= 60) return { label: '正常', color: 'text-white/70', bg: 'bg-white/10' };
  return { label: '待改善', color: 'text-rose-300', bg: 'bg-rose-400/20' };
}

// Dashboard缓存配置
const DASHBOARD_CACHE_KEY = 'dashboard_data_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30分钟

interface DashboardCacheData {
  timestamp: number;
  familyId: string;
  data: DashboardResponse;
}

function getDashboardCache(familyId: string): DashboardResponse | null {
  try {
    const cached = localStorage.getItem(DASHBOARD_CACHE_KEY);
    if (!cached) return null;
    const cacheData: DashboardCacheData = JSON.parse(cached);
    if (cacheData.familyId !== familyId) return null;
    if (Date.now() - cacheData.timestamp > CACHE_DURATION) return null;
    return cacheData.data;
  } catch {
    return null;
  }
}

function setDashboardCache(familyId: string, data: DashboardResponse) {
  try {
    const cacheData: DashboardCacheData = {
      timestamp: Date.now(),
      familyId,
      data
    };
    localStorage.setItem(DASHBOARD_CACHE_KEY, JSON.stringify(cacheData));
  } catch {
    // 忽略缓存写入错误
  }
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { currentFamily } = useAuth();
  // 从共享Context获取数据（只调用一次API）
  const { children: apiChildren, rewardRules: apiRewardRules, punishRules: apiPunishRules, ensureRulesLoaded } = useFamilyData();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  const [checkingIn, setCheckingIn] = useState<string | null>(null);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [recordForm, setRecordForm] = useState<RecordForm>({ childId: '', ruleId: '', note: '' });
  const [recordSuccess, setRecordSuccess] = useState(false);
  const [checklistChildId, setChecklistChildId] = useState<string | 'all'>('all');
  const [showFamilySwitcherLocal, setShowFamilySwitcherLocal] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);

  // 加载dashboard数据（使用缓存）
  useEffect(() => {
    ensureRulesLoaded(); // 延迟加载规则（用于快速记录）
    const fetchDashboard = async () => {
      if (!currentFamily?.id) return;

      // 1. 尝试从缓存加载
      const cachedData = getDashboardCache(currentFamily.id);
      if (cachedData) {
        setDashboardData(cachedData);
        setLoading(false);
        // 后台刷新
        refreshDashboard(currentFamily.id);
        return;
      }

      // 2. 无缓存，首次加载
      setLoading(true);
      try {
        const res = await api.dashboard.getDashboard(currentFamily.id);
        if (res.code === 0) {
          setDashboardData(res.data);
          setDashboardCache(currentFamily.id, res.data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [currentFamily?.id]);

  // 后台刷新dashboard数据
  const refreshDashboard = async (familyId: string) => {
    try {
      const res = await api.dashboard.getDashboard(familyId);
      if (res.code === 0) {
        setDashboardData(res.data);
        setDashboardCache(familyId, res.data);
      }
    } catch (error) {
      console.error('Failed to refresh dashboard:', error);
    }
  };

  // 从dashboardData提取数据
  const dashboardChildren = dashboardData?.children || [];
  const todayCheckins = dashboardData?.today_checkins || [];
  const recentRecords = dashboardData?.recent_records || [];
  const weekTrend = dashboardData?.week_trend;

  // 构建儿童显示数据
  const children = useMemo(() => {
    return dashboardChildren.map(child => {
      const apiChild = apiChildren.find(c => c.id === child.id);
      const pointsTrend = weekTrend?.children?.find(c => c.id === child.id)?.points || [0, 0, 0, 0, 0, 0, 0];
      const dates = weekTrend?.dates || ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];

      return {
        id: child.id,
        name: child.name,
        nickName: apiChild?.nickname || child.name,
        avatar: child.avatar ? getStaticUrl(child.avatar) : '',
        age: apiChild?.birthday ? Math.floor((Date.now() - new Date(apiChild.birthday).getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 6,
        gender: apiChild?.gender === 1 ? '男' : apiChild?.gender === 2 ? '女' : '未知',
        school: apiChild?.school || '',
        grade: apiChild?.class_name || '',
        birthday: apiChild?.birthday || '',
        avatarBg: child.avatar_color || '#3B82F6',
        totalPoints: child.points || 0,
        totalEarned: child.total_reward || 0,
        totalSpent: child.total_punish || 0,
        level: child.level?.level || 1,
        levelName: child.level?.name || '新手',
        levelProgress: child.level?.progress || 0,
        nextLevelName: '下一级',
        consecutiveCheckIn: child.checkin_days || 0,
        todayPoints: child.today_points || 0,
        experience: child.experience || 0,
        todayRewards: 0,
        todayPunishments: 0,
        overallScore: Math.round(child.habit_score ?? 0),
        habitScores: {
          timeManagement: 75,
          studyFocus: 70,
          lifeCare: 80,
          sportsHealth: 72,
          responsibility: 68,
          etiquette: 75
        },
        todayTasks: todayCheckins.filter(t => t.child_id === child.id).map(t => ({
          id: t.id,
          name: t.rule_name,
          completed: t.status === 1,
          points: t.points || 0,
          time: t.completed_at ? new Date(t.completed_at).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }) : ''
        })),
        pointsTrend: dates.map((date, idx) => ({
          date,
          points: pointsTrend[idx] || 0
        })),
        dailyTrend15: []
      };
    });
  }, [dashboardChildren, apiChildren, todayCheckins, weekTrend]);

  // 家庭数据
  const familyData = useMemo(() => {
    if (!dashboardData) return null;
    const pointsName = dashboardData.family.point_name || '星星';
    return {
      id: dashboardData.family.id,
      name: dashboardData.family.name,
      membershipLevel: dashboardData.family.vip_status === 2 ? 'VIP' : '普通',
      pointsName,
      pointsIcon: getPointsIcon(pointsName),
      exchangeRate: 1,
      overallScore: Math.round(dashboardData.family.habit_score ?? 0),
      platformPercentage: dashboardData.family.exceed_percent ?? 0,
      totalChildren: children.length,
      totalAdults: 2,
      totalPoints: children.reduce((sum, c) => sum + (c.totalPoints || 0), 0),
      todayRewardCount: dashboardData.today_stats?.reward_count || 0,
      todayPunishmentCount: dashboardData.today_stats?.punish_count || 0,
      todayNetPoints: dashboardData.today_stats?.net_points || 0,
      todayActivities: 0,
      weekActivities: 0
    };
  }, [dashboardData, children]);

  // 最近记录
  const displayRecords = useMemo(() => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = `${yesterday.getFullYear()}-${(yesterday.getMonth() + 1).toString().padStart(2, '0')}-${yesterday.getDate().toString().padStart(2, '0')}`;

    return recentRecords.slice(0, 5).map(record => {
      // 后端存储的是 UTC+8 时间，直接解析即可
      const recordDate = new Date(record.created_at);
      const dateStr = `${recordDate.getFullYear()}-${(recordDate.getMonth() + 1).toString().padStart(2, '0')}-${recordDate.getDate().toString().padStart(2, '0')}`;
      let dateLabel = `${recordDate.getMonth() + 1}-${recordDate.getDate()}`;
      if (dateStr === todayStr) dateLabel = '今天';
      else if (dateStr === yesterdayStr) dateLabel = '昨天';

      return {
        id: record.id,
        childId: record.child_id,
        childName: record.child_name || '',
        childNickname: record.child_nickname || '',
        childAvatar: record.child_avatar ? getStaticUrl(record.child_avatar) : '',
        childAvatarBg: record.avatar_color || '#3B82F6',
        type: record.type === 1 ? 'reward' : record.type === 2 ? 'punishment' : 'redeem',
        ruleName: record.rule_name || '自定义',
        points: record.points || 0,
        note: record.remark || '',
        channel: record.source || 'PC',
        time: `${recordDate.getHours().toString().padStart(2, '0')}:${recordDate.getMinutes().toString().padStart(2, '0')}`,
        date: dateLabel,
      };
    });
  }, [recentRecords]);

  // 获取当前选中儿童今日已打卡的规则ID集合
  const getTodayCompletedRuleIds = (childId: string) => {
    return new Set(
      todayCheckins
        .filter(item => item.child_id === childId && item.status === 1)
        .map(item => item.rule_id)
    );
  };

  // 根据弹窗类型过滤规则
  const filteredRules = useMemo(() => {
    const completedRuleIds = getTodayCompletedRuleIds(recordForm.childId);
    // 根据modalType选择对应的规则列表
    const rules = modalType === 'reward' ? apiRewardRules : apiPunishRules;
    // 过滤掉已打卡的规则
    return rules.filter(rule => !completedRuleIds.has(rule.id));
  }, [modalType, apiRewardRules, apiPunishRules, recordForm.childId, todayCheckins]);

  // 打卡清单
  const checklistTasks = useMemo(() => {
    return checklistChildId === 'all'
      ? todayCheckins
      : todayCheckins.filter(item => item.child_id === checklistChildId);
  }, [todayCheckins, checklistChildId]);

  const checkedCount = checklistTasks.filter(t => t.status === 1).length;
  const checkedPoints = checklistTasks.filter(t => t.status === 1).reduce((a, t) => a + (t.points || 0), 0);

  // 周趋势图表数据
  const weekDays = weekTrend?.dates || ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
  const allPts = children.flatMap(c => c.pointsTrend.map(p => p.points));
  const maxPts = Math.max(...allPts, 1);
  const minPts = Math.min(...allPts, 0);
  const range = maxPts - minPts || 1;
  const chartH = 140;
  const barW = 14;
  const gap = 3;
  const groupW = children.length * (barW + gap) + 6;
  const totalW = weekDays.length * (groupW + 10);

  const getY = (pts: number) => {
    const ratio = (maxPts - pts) / range;
    return 10 + ratio * (chartH - 20);
  };
  const zeroY = getY(0);

  const childColors = ['#3B82F6', '#F59E0B'];
  const childColorsBg = ['bg-blue-500', 'bg-amber-400'];

  const scoreInfo = familyData ? getScoreLabel(familyData.overallScore) : { label: '加载中', color: 'text-white/70', bg: 'bg-white/10' };

  // 快捷操作
  const quickActions = [
    {
      icon: 'ri-trophy-fill',
      label: '记录奖励',
      color: 'from-emerald-400 to-teal-500',
      textColor: 'text-emerald-700',
      action: () => {
        setModalType('reward');
        setRecordForm({ childId: children[0]?.id || '', ruleId: '', note: '' });
      }
    },
    {
      icon: 'ri-alert-fill',
      label: '记录惩罚',
      color: 'from-rose-400 to-rose-500',
      textColor: 'text-rose-700',
      action: () => {
        setModalType('punishment');
        setRecordForm({ childId: children[0]?.id || '', ruleId: '', note: '' });
      }
    },
    {
      icon: 'ri-exchange-funds-fill',
      label: '积分兑换',
      color: 'from-amber-400 to-yellow-500',
      textColor: 'text-amber-700',
      action: () => navigate('/rewards?tab=store')
    },
    {
      icon: 'ri-robot-2-fill',
      label: 'AI记录',
      color: 'from-blue-400 to-cyan-500',
      textColor: 'text-blue-700',
      action: () => navigate('/ai')
    }
  ];

  // 提交记录
  const handleSubmitRecord = async () => {
    if (!recordForm.childId) {
      alert('请先选择儿童');
      return;
    }
    if (!recordForm.ruleId) {
      alert('请先选择规则');
      return;
    }
    if (!currentFamily?.id) return;

    try {
      setRecordSuccess(true);

      let selectedRule: any = null;
      if (modalType === 'reward') {
        selectedRule = apiRewardRules.find(r => r.id === recordForm.ruleId);
      } else {
        selectedRule = apiPunishRules.find(r => r.id === recordForm.ruleId);
      }

      if (!selectedRule) return;

      if (modalType === 'reward') {
        await api.records.recordReward({
          family_id: currentFamily.id,
          child_id: recordForm.childId,
          rule_id: recordForm.ruleId,
          points: Number(selectedRule.points),
          rule_name: selectedRule.name,
          habit_type: selectedRule.habit_type,
          remark: recordForm.note || undefined,
          source: 'pc',
          bonus_points: 0
        });
      } else {
        await api.records.recordPunish({
          family_id: currentFamily.id,
          child_id: recordForm.childId,
          rule_id: recordForm.ruleId,
          points: Number(selectedRule.points),
          rule_name: selectedRule.name,
          habit_type: selectedRule.habit_type,
          remark: recordForm.note || undefined,
          source: 'pc'
        });
      }

      // 刷新数据
      await refreshDashboard(currentFamily.id);
      setModalType(null);
      setRecordSuccess(false);
    } catch (error) {
      console.error('Failed to submit record:', error);
      alert('记录失败，请重试');
      setRecordSuccess(false);
    }
  };

  // 打卡
  const handleCheckIn = async (checkinId: string) => {
    if (!currentFamily?.id) return;
    setCheckingIn(checkinId);
    try {
      await api.records.completeCheckin(checkinId);
      await refreshDashboard(currentFamily.id);
    } catch (error) {
      console.error('Failed to check in:', error);
    } finally {
      setCheckingIn(null);
    }
  };

  // Loading状态
  if (loading && !dashboardData) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-blue-500 mb-3"></i>
            <p className="text-gray-500">加载中...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!familyData) return null;

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-black text-blue-900">家庭总览</h1>
            <p className="text-gray-400 text-sm mt-0.5">{new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/settings')} className="w-9 h-9 flex items-center justify-center bg-white rounded-xl text-gray-400 hover:text-blue-500 cursor-pointer transition-colors border border-blue-100">
              <i className="ri-settings-4-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Family Score Card */}
        <div
          className="rounded-2xl p-4 md:p-5 mb-5 relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 50%, #1E88E5 100%)' }}
        >
          <div className="absolute -right-10 -top-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none"></div>
          <div className="absolute right-10 -bottom-12 w-36 h-36 bg-yellow-400/10 rounded-full pointer-events-none"></div>
          <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
            <button
              onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('open-family-switcher')); }}
              className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 active:bg-white/30 rounded-xl px-3 py-2 transition-colors cursor-pointer border border-white/20"
            >
              <i className="ri-swap-line text-white text-sm"></i>
              <span className="text-white text-xs font-medium whitespace-nowrap">切换家庭</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigate('/vip'); }}
              className="flex items-center gap-1.5 bg-gradient-to-r from-yellow-400/30 to-amber-400/30 hover:from-yellow-400/50 hover:to-amber-400/50 active:from-yellow-400/60 border border-yellow-300/40 rounded-xl px-3 py-2 transition-colors cursor-pointer"
            >
              <i className="ri-vip-crown-fill text-yellow-300 text-sm"></i>
              <span className="text-yellow-100 text-xs font-medium whitespace-nowrap">会员中心</span>
            </button>
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <i className="ri-home-heart-fill text-white/60 text-sm flex-shrink-0"></i>
                <p className="text-white/60 text-xs md:text-sm truncate">{familyData.name} · 家庭综合评分</p>
              </div>
            </div>
            <div className="flex items-end gap-3 mb-4">
              <span className="text-5xl md:text-6xl font-black text-white leading-none">{familyData.overallScore}</span>
              <div className="mb-1 space-y-1">
                <span className={`inline-block text-xs font-bold px-2.5 py-0.5 rounded-full ${scoreInfo.bg} ${scoreInfo.color}`}>{scoreInfo.label}</span>
                <p className="text-white/50 text-xs">超过 <span className="text-yellow-300 font-bold">{familyData.platformPercentage}%</span> 家庭</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2.5">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-400/30 flex-shrink-0">
                  <i className="ri-add-circle-fill text-emerald-300 text-sm"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-white font-black text-lg leading-none">{familyData.todayRewardCount}</p>
                  <p className="text-white/50 text-[10px] whitespace-nowrap">今日奖励</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2.5">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-400/30 flex-shrink-0">
                  <i className="ri-indeterminate-circle-fill text-rose-300 text-sm"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-white font-black text-lg leading-none">{familyData.todayPunishmentCount}</p>
                  <p className="text-white/50 text-[10px] whitespace-nowrap">今日惩罚</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2.5">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-yellow-400/30 flex-shrink-0">
                  <i className={`${familyData.pointsIcon} text-yellow-300 text-sm`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-white font-black text-lg leading-none">{familyData.todayNetPoints >= 0 ? '+' : ''}{familyData.todayNetPoints}</p>
                  <p className="text-white/50 text-[10px] whitespace-nowrap">净积分</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-2 md:gap-3 mb-5">
          {quickActions.map((a, i) => (
            <button
              key={i}
              onClick={a.action}
              className="flex flex-col items-center gap-1.5 p-2.5 md:p-3 bg-white rounded-2xl hover:-translate-y-0.5 transition-all cursor-pointer group border border-blue-100"
            >
              <div className={`w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-xl bg-gradient-to-br ${a.color} group-hover:scale-110 transition-transform`}>
                <i className={`${a.icon} text-white text-lg md:text-xl`}></i>
              </div>
              <span className={`text-[11px] md:text-xs font-semibold ${a.textColor} whitespace-nowrap`}>{a.label}</span>
            </button>
          ))}
        </div>

        {/* Children Cards */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-user-smile-fill text-blue-500 text-base"></i>
            <h2 className="font-bold text-blue-900 text-base">孩子们的表现</h2>
          </div>
          <button onClick={() => navigate('/members')} className="text-xs text-blue-600 font-medium hover:underline cursor-pointer whitespace-nowrap flex items-center gap-1">
            查看全部 <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
        {children.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 border border-dashed border-blue-200 mb-5 text-center cursor-pointer hover:border-blue-400 transition-colors" onClick={() => navigate('/members')}>
            <div className="w-14 h-14 flex items-center justify-center bg-blue-50 rounded-2xl mx-auto mb-3">
              <i className="ri-user-add-line text-2xl text-blue-300"></i>
            </div>
            <p className="text-gray-500 text-sm font-medium">家庭未添加儿童信息</p>
            <p className="text-blue-500 text-xs mt-1 font-medium">请点击前往添加 →</p>
          </div>
        ) : (
        <div className={`grid gap-4 mb-5 ${children.length >= 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
          {children.map((child, idx) => (
            <div key={child.id} className="bg-white rounded-2xl p-4 border border-blue-100 hover:border-blue-300 transition-all cursor-pointer" onClick={() => navigate(`/analytics?child=${child.id}`)}>
              <div className="flex items-center gap-3 mb-3">
                <div className="relative flex-shrink-0">
                  {child.avatar ? (
                    <img src={child.avatar} alt={child.name} className="w-12 h-12 rounded-2xl object-cover object-top border-2 border-blue-100" />
                  ) : (
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-lg border-2 border-blue-100" style={{ background: child.avatarBg }}>
                      {child.nickName || child.name || '孩'}
                    </div>
                  )}
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full border-2 border-white text-white text-[8px] font-black"
                    style={{ background: 'linear-gradient(135deg, #1E88E5, #1565C0)' }}>
                    {child.level}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-bold text-blue-900 text-sm">{child.name}</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full whitespace-nowrap">{child.levelName}</span>
                  </div>
                  <p className="text-gray-400 text-xs">{child.age}岁 · {child.gender}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${child.levelProgress}%`, background: 'linear-gradient(90deg,#1E88E5,#1565C0)' }}></div>
                    </div>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{child.levelProgress}%→{child.nextLevelName}</span>
                  </div>
                </div>
              </div>

              {/* Current Points */}
              <div className={`rounded-xl px-4 py-2.5 mb-3 flex items-center justify-between border ${idx === 0 ? 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-100' : 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-100'}`}>
                <div className="flex items-center gap-2">
                  <i className={`${familyData.pointsIcon} text-lg ${idx === 0 ? 'text-blue-400' : 'text-amber-400'}`}></i>
                  <span className={`text-2xl font-black ${idx === 0 ? 'text-blue-800' : 'text-amber-800'}`}>{child.totalPoints}</span>
                  <span className={`text-sm font-medium ${idx === 0 ? 'text-blue-600' : 'text-amber-600'}`}>{familyData.pointsName}</span>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-400">连续打卡 <span className={`font-bold ${idx === 0 ? 'text-blue-600' : 'text-amber-600'}`}>{child.consecutiveCheckIn}天</span></div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="bg-emerald-50 rounded-xl p-2.5 text-center border border-emerald-100">
                  <div className="flex items-center justify-center gap-0.5 mb-0.5">
                    <i className="ri-arrow-up-fill text-emerald-400 text-xs"></i>
                    <span className="text-emerald-600 font-black text-base leading-none">{child.totalEarned}</span>
                  </div>
                  <div className="text-gray-400 text-[10px]">总获得</div>
                </div>
                <div className="bg-rose-50 rounded-xl p-2.5 text-center border border-rose-100">
                  <div className="flex items-center justify-center gap-0.5 mb-0.5">
                    <i className="ri-arrow-down-fill text-rose-400 text-xs"></i>
                    <span className="text-rose-600 font-black text-base leading-none">{child.totalSpent}</span>
                  </div>
                  <div className="text-gray-400 text-[10px]">总消耗</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-2.5 text-center border border-blue-100">
                  <div className="flex items-center justify-center gap-0.5 mb-0.5">
                    <i className="ri-award-fill text-blue-400 text-xs"></i>
                    <span className="text-blue-600 font-black text-base leading-none">{child.experience}</span>
                  </div>
                  <div className="text-gray-400 text-[10px]">经验</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Checklist + Weekly Trend */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          {/* Today checklist */}
          <div className="bg-white rounded-2xl p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-emerald-500 text-base"></i>
                <h2 className="font-bold text-blue-900 text-base">今日打卡清单</h2>
              </div>
              <div className="flex gap-1 bg-blue-50 p-0.5 rounded-lg border border-blue-100">
                <button
                  onClick={() => setChecklistChildId('all')}
                  className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all cursor-pointer whitespace-nowrap ${
                    checklistChildId === 'all' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
                  }`}
                >全部</button>
                {children.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setChecklistChildId(c.id)}
                    className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all cursor-pointer whitespace-nowrap ${
                      checklistChildId === c.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
                    }`}
                  >{c.nickName}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2 overflow-y-auto pr-0.5" style={{ maxHeight: 'calc(6 * 52px)' }}>
              {checklistTasks.map(task => {
                const done = task.status === 1;
                const isCheckingIn = checkingIn === task.id;
                return (
                  <button
                    key={task.id}
                    onClick={() => !done && !isCheckingIn && handleCheckIn(task.id)}
                    disabled={done || isCheckingIn}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer text-left ${
                      done ? 'bg-emerald-50' :
                      isCheckingIn ? 'bg-blue-100' :
                      'bg-blue-50/50 hover:bg-blue-50'
                    } ${(done || isCheckingIn) ? 'cursor-not-allowed opacity-75' : ''}`}
                  >
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${
                      done ? 'bg-emerald-500' :
                      isCheckingIn ? 'bg-blue-400 animate-pulse' :
                      'border-2 border-blue-200'
                    }`}>
                      {done ? (
                        <i className="ri-check-line text-white text-xs"></i>
                      ) : isCheckingIn ? (
                        <i className="ri-loader-4-line text-white text-xs animate-spin"></i>
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-sm ${done ? 'line-through text-gray-400' : 'text-gray-700'}`}>{task.rule_name}</span>
                        {checklistChildId === 'all' && (
                          <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">{task.child_name}</span>
                        )}
                      </div>
                    </div>
                    <div className={`flex items-center gap-0.5 text-xs font-medium flex-shrink-0 ${done ? 'text-emerald-600' : 'text-gray-400'}`}>
                      <i className={`${familyData.pointsIcon} text-amber-400 text-xs`}></i>
                      +{task.points}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-blue-50 flex items-center justify-between text-xs text-gray-400">
              <span>已完成 {checkedCount} / {checklistTasks.length} 项</span>
              <span className="text-emerald-500 font-semibold">+{checkedPoints} 积分</span>
            </div>
          </div>

          {/* Weekly trend */}
          <div className="bg-white rounded-2xl p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <i className="ri-line-chart-fill text-blue-500 text-base"></i>
                <h2 className="font-bold text-blue-900 text-base">本周积分趋势</h2>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                {children.map((c, idx) => (
                  <div key={c.id} className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${childColorsBg[idx]}`}></div>
                    <span>{c.nickName}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <svg viewBox={`0 0 ${totalW + 20} ${chartH + 50}`} className="w-full" style={{ minWidth: '320px', height: '242px' }}>
                {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                  const y = 10 + ratio * (chartH - 20);
                  return (
                    <line key={i}
                      x1="10" y1={y}
                      x2={totalW + 10} y2={y}
                      stroke="#DBEAFE" strokeWidth="1" strokeDasharray="3,3"
                    />
                  );
                })}
                <line x1="10" y1={zeroY} x2={totalW + 10} y2={zeroY} stroke="#93C5FD" strokeWidth="1.5" />
                {weekDays.map((day, di) => {
                  const gx = 10 + di * (groupW + 10);
                  return (
                    <g key={di}>
                      {children.map((child, ci) => {
                        const pts = child.pointsTrend[di].points;
                        const barY = getY(pts);
                        const barH = Math.max(4, Math.abs(zeroY - barY));
                        const bx = gx + ci * (barW + gap);
                        const by = pts >= 0 ? barY : zeroY;
                        const fill = childColors[ci];
                        const textY = pts >= 0 ? barY - 3 : barY + barH + 10;
                        return (
                          <g key={ci}>
                            <rect x={bx} y={by} width={barW} height={barH} rx="3" fill={fill} opacity="0.85" />
                            <text x={bx + barW / 2} y={textY} textAnchor="middle" fontSize="8" fill={fill} fontWeight="bold">
                              {pts > 0 ? `+${pts}` : pts}
                            </text>
                          </g>
                        );
                      })}
                      <text x={gx + (children.length * (barW + gap)) / 2 - 2} y={chartH + 38} textAnchor="middle" fontSize="10" fill="#6B7280">{day}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
            <div className="pt-3 border-t border-blue-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400 font-medium">本周净积分</span>
                <span className="text-xs font-bold text-blue-900">
                  全家合计 {children.reduce((s, c) => s + c.pointsTrend.reduce((a, p) => a + p.points, 0), 0)} 分
                </span>
              </div>
              <div className="flex gap-3">
                {children.map((child, idx) => {
                  const net = child.pointsTrend.reduce((a, p) => a + p.points, 0);
                  return (
                    <div key={child.id} className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl ${idx === 0 ? 'bg-blue-50 border border-blue-100' : 'bg-amber-50 border border-amber-100'}`}>
                      {child.avatar ? (
                        <img src={child.avatar} alt={child.name} className="w-6 h-6 rounded-lg object-cover object-top flex-shrink-0" />
                      ) : (
                        <div className="w-6 h-6 rounded-lg flex items-center justify-center text-white font-black text-[8px] flex-shrink-0 leading-tight" style={{ background: child.avatarBg }}>
                          {(child.nickName || child.name || '?').slice(0, 2)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-[10px] text-gray-400 truncate">{child.nickName}</div>
                        <div className={`text-sm font-black ${net >= 0 ? (idx === 0 ? 'text-blue-600' : 'text-amber-600') : 'text-gray-400'}`}>
                          {net >= 0 ? '+' : ''}{net}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Records */}
        <div className="bg-white rounded-2xl p-4 border border-blue-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <i className="ri-history-fill text-amber-500 text-base"></i>
              <h2 className="font-bold text-blue-900 text-base">最近奖惩记录</h2>
            </div>
            <button onClick={() => navigate('/records')} className="text-xs text-blue-600 font-medium hover:underline cursor-pointer whitespace-nowrap flex items-center gap-1">
              查看全部 <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
          <div className="space-y-2">
            {displayRecords.map(rec => {
              const ch = channelConfig[rec.channel] || channelConfig['PC'];
              const tc = typeConfig[rec.type] || typeConfig['reward'];
              return (
                <div key={rec.id} className="flex items-center gap-3 p-3 bg-blue-50/40 rounded-xl hover:bg-blue-50 transition-colors">
                  <div className="relative flex-shrink-0">
                    {rec.childAvatar ? (
                      <img src={rec.childAvatar} alt={rec.childName} className="w-9 h-9 rounded-xl object-cover object-top border-2 border-blue-100" />
                    ) : (
                      <div className="w-9 h-9 rounded-xl border-2 border-blue-100 flex items-center justify-center text-white font-black text-[10px]" style={{ backgroundColor: rec.childAvatarBg }}>
                        {rec.childNickname || rec.childName || '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span className="text-xs font-bold text-blue-900">{rec.childName}</span>
                      <span className="text-xs text-gray-500 truncate max-w-[100px]">{rec.ruleName}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${tc.bg} ${tc.text}`}>{tc.label}</span>
                      <span className={`flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${ch.bg} ${ch.text}`}>
                        <i className={`${ch.icon} text-[9px]`}></i>{rec.channel}
                      </span>
                    </div>
                    {rec.note && <p className="text-[10px] text-gray-400 truncate">{rec.note}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                    <span className={`font-black text-sm ${rec.points > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {rec.points > 0 ? `+${rec.points}` : rec.points}
                    </span>
                    <div className="text-[10px] text-gray-400 whitespace-nowrap">{rec.date} {rec.time}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Record Modal */}
      {modalType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setModalType(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[616px] max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-blue-900 text-lg">
                {modalType === 'reward' ? '记录奖励' : '记录惩罚'}
              </h3>
              <button onClick={() => setModalType(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2.5 block">选择儿童</label>
                <div className="flex gap-2.5 flex-wrap">
                  {children.map(child => (
                    <button key={child.id} onClick={() => setRecordForm(prev => ({ ...prev, childId: child.id }))}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border-2 transition-all cursor-pointer ${
                        recordForm.childId === child.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-200'
                      }`}>
                      {child.avatar ? (
                        <img src={child.avatar} alt={child.name} className="w-8 h-8 rounded-lg object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-black text-[10px] leading-tight" style={{ background: child.avatarBg }}>
                          {(child.nickName || child.name || '?').slice(0, 2)}
                        </div>
                      )}
                      <div className="text-left">
                        <span className="text-sm font-medium block">{child.name}</span>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <i className={`${familyData.pointsIcon} text-amber-400`}></i>
                          {child.totalPoints} {familyData.pointsName}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2.5 block">
                  选择{modalType === 'reward' ? '奖励' : '惩罚'}规则
                  <span className="text-xs font-normal text-gray-400 ml-2">（已排除今日已打卡的规则）</span>
                </label>
                <div className="grid grid-cols-3 gap-2.5 max-h-60 overflow-y-auto pr-1">
                  {filteredRules.map(rule => (
                    <button key={rule.id} onClick={() => setRecordForm(prev => ({ ...prev, ruleId: rule.id, note: rule.description || '' }))}
                      className={`text-left p-3.5 rounded-xl border-2 transition-all cursor-pointer ${
                        recordForm.ruleId === rule.id
                          ? (modalType === 'reward' ? 'border-emerald-500 bg-emerald-50' : 'border-rose-500 bg-rose-50')
                          : 'border-gray-200 hover:border-blue-200'
                      }`}>
                      <p className="text-sm font-medium text-blue-900 truncate">{rule.name}</p>
                      <p className={`text-xs mt-1 font-semibold flex items-center gap-1 ${modalType === 'reward' ? 'text-emerald-600' : 'text-rose-600'}`}>
                        <i className={`${familyData.pointsIcon} text-[10px]`}></i>
                        {modalType === 'reward' ? '+' : '-'}{rule.points} {familyData.pointsName}
                      </p>
                    </button>
                  ))}
                  {filteredRules.length === 0 && (
                    <div className="col-span-3 text-center py-4 text-gray-400 text-sm">
                      暂无可用的{modalType === 'reward' ? '奖励' : '惩罚'}规则
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2.5 block">备注</label>
                <textarea value={recordForm.note} onChange={e => setRecordForm(prev => ({ ...prev, note: e.target.value }))}
                  placeholder="可选备注信息..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-400 resize-none" rows={2} />
              </div>
              <button onClick={handleSubmitRecord} disabled={recordSuccess}
                className={`w-full py-3.5 rounded-xl font-bold text-white transition-all cursor-pointer whitespace-nowrap text-base ${
                  modalType === 'reward' ? 'bg-gradient-to-r from-emerald-400 to-teal-500' : 'bg-gradient-to-r from-rose-400 to-rose-500'
                } ${recordSuccess ? 'opacity-50' : 'hover:opacity-90'}`}>
                {recordSuccess ? '提交中...' : '确认记录'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
